import React, { useState, useEffect, useCallback } from 'react';
import { 
  TrendingUp, TrendingDown, Play, Square, Settings, 
  Activity, DollarSign, BarChart3, AlertTriangle,
  RefreshCw, Wifi, WifiOff
} from 'lucide-react';
import axios from 'axios';
import MarketData from './MarketData';
import TradingConfig from './TradingConfig';
import Positions from './Positions';
import TradingHistory from './TradingHistory';

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [tradingStatus, setTradingStatus] = useState({
    enabled: false,
    last_update: null,
    positions_count: 0,
    config: null
  });
  const [marketData, setMarketData] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  
  // WebSocket connection for real-time updates
  useEffect(() => {
    const connectWebSocket = () => {
      const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('http', 'ws') || 'ws://localhost:8001';
      const ws = new WebSocket(`${wsUrl}/ws`);

      ws.onopen = () => {
        setWsConnected(true);
        console.log('WebSocket connected');
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'status_update') {
          setTradingStatus(prev => ({
            ...prev,
            enabled: data.trading_enabled,
            positions_count: data.positions_count,
            last_update: data.last_update
          }));
        }
      };

      ws.onclose = () => {
        setWsConnected(false);
        console.log('WebSocket disconnected');
        // Reconnect after 5 seconds
        setTimeout(connectWebSocket, 5000);
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };

      return ws;
    };

    const ws = connectWebSocket();
    return () => ws.close();
  }, []);

  const fetchTradingStatus = useCallback(async () => {
    try {
      const response = await axios.get('/api/trading/status');
      setTradingStatus(response.data);
    } catch (err) {
      setError('Failed to fetch trading status');
    }
  }, []);

  const fetchMarketData = useCallback(async () => {
    try {
      const response = await axios.get('/api/market-data');
      setMarketData(response.data.data || []);
    } catch (err) {
      setError('Failed to fetch market data');
    }
  }, []);

  const fetchPositions = useCallback(async () => {
    try {
      const response = await axios.get('/api/positions');
      setPositions(response.data.positions || []);
    } catch (err) {
      console.error('Failed to fetch positions:', err);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchTradingStatus(),
        fetchMarketData(),
        fetchPositions()
      ]);
      setLoading(false);
    };

    loadData();
    
    // Refresh data every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [fetchTradingStatus, fetchMarketData, fetchPositions]);

  const handleStartTrading = async () => {
    try {
      await axios.post('/api/trading/start');
      await fetchTradingStatus();
    } catch (err) {
      setError('Failed to start trading: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleStopTrading = async () => {
    try {
      await axios.post('/api/trading/stop');
      await fetchTradingStatus();
    } catch (err) {
      setError('Failed to stop trading: ' + (err.response?.data?.detail || err.message));
    }
  };

  const refreshData = async () => {
    await Promise.all([
      fetchTradingStatus(),
      fetchMarketData(),
      fetchPositions()
    ]);
  };

  // Calculate portfolio summary
  const portfolioSummary = {
    totalPnL: positions.reduce((sum, pos) => sum + (pos.pnl || 0), 0),
    totalValue: positions.reduce((sum, pos) => sum + (pos.size * pos.current_price || 0), 0),
    openPositions: positions.length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-white text-lg">Loading Drbleou Trading Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 pt-16">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-900/50 border border-red-700 rounded-lg p-4 flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-400 mr-3" />
            <span className="text-red-300">{error}</span>
            <button 
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-300"
            >
              ×
            </button>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Professional Trading Dashboard</h1>
            <p className="text-gray-400">Drbleou Advanced Trading Automation</p>
          </div>
          
          <div className="flex items-center space-x-4 mt-4 lg:mt-0">
            {/* Connection Status */}
            <div className="flex items-center">
              {wsConnected ? (
                <Wifi className="w-5 h-5 text-green-400 mr-2" />
              ) : (
                <WifiOff className="w-5 h-5 text-red-400 mr-2" />
              )}
              <span className={`text-sm ${wsConnected ? 'text-green-400' : 'text-red-400'}`}>
                {wsConnected ? 'Connected' : 'Disconnected'}
              </span>
            </div>

            {/* Refresh Button */}
            <button
              onClick={refreshData}
              className="flex items-center px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>

            {/* Trading Controls */}
            <button
              onClick={tradingStatus.enabled ? handleStopTrading : handleStartTrading}
              className={`flex items-center px-6 py-2 rounded-lg font-medium ${
                tradingStatus.enabled
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              {tradingStatus.enabled ? (
                <>
                  <Square className="w-4 h-4 mr-2" />
                  Stop Trading
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Start Trading
                </>
              )}
            </button>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Trading Status */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Trading Status</p>
                <p className={`text-lg font-semibold ${
                  tradingStatus.enabled ? 'text-green-400' : 'text-red-400'
                }`}>
                  {tradingStatus.enabled ? 'Active' : 'Stopped'}
                </p>
              </div>
              <Activity className={`w-8 h-8 ${
                tradingStatus.enabled ? 'text-green-400' : 'text-red-400'
              }`} />
            </div>
          </div>

          {/* Open Positions */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Open Positions</p>
                <p className="text-lg font-semibold text-white">
                  {portfolioSummary.openPositions}
                </p>
              </div>
              <BarChart3 className="w-8 h-8 text-blue-400" />
            </div>
          </div>

          {/* Total P&L */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total P&L</p>
                <p className={`text-lg font-semibold ${
                  portfolioSummary.totalPnL >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  ${portfolioSummary.totalPnL.toFixed(2)}
                </p>
              </div>
              {portfolioSummary.totalPnL >= 0 ? (
                <TrendingUp className="w-8 h-8 text-green-400" />
              ) : (
                <TrendingDown className="w-8 h-8 text-red-400" />
              )}
            </div>
          </div>

          {/* Portfolio Value */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Portfolio Value</p>
                <p className="text-lg font-semibold text-white">
                  ${portfolioSummary.totalValue.toFixed(2)}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-yellow-400" />
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex space-x-1 mb-6 bg-gray-800 rounded-lg p-1">
          {[
            { id: 'overview', label: 'Market Overview', icon: BarChart3 },
            { id: 'positions', label: 'Positions', icon: Activity },
            { id: 'config', label: 'Configuration', icon: Settings },
            { id: 'history', label: 'History', icon: TrendingUp }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-gray-800 rounded-xl border border-gray-700">
          {activeTab === 'overview' && <MarketData data={marketData} />}
          {activeTab === 'positions' && <Positions positions={positions} onRefresh={fetchPositions} />}
          {activeTab === 'config' && <TradingConfig config={tradingStatus.config} onUpdate={fetchTradingStatus} />}
          {activeTab === 'history' && <TradingHistory />}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;